import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { createTask, updateTask } from 'bundles/apex/playbooks/config/store/playbooks/actions';
import { getPlaybookRecordType, getPlaybookType, isDealPlaybook } from 'bundles/apex/playbooks/config/helpers';
import { titleize } from 'helpers/formatters/string_formats';

import Assignee from './assignee';
import { Button } from 'components/ui/button';
import DueDates from './due_dates';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import ManagedInput from 'components/form/managed_input';
import Modal from 'components/modal';
import { Errors } from 'components/ui/errors';

import './styles.scss';

export function TaskModal({
  canRequirePlaybookTasks,
  createTask,
  offices = [],
  playbook,
  stage,
  stages,
  status,
  task,
  updateTask,
  users,
  onHide,
}) {
  stage = stage || stages[task.stage];
  status = status || task.status;
  const targetState = isDealPlaybook(playbook) ? stage : status;
  const [name, setName] = useState(task?.name);
  const [required, setRequired] = useState(task?.required ? 'true' : 'false');
  const [requiresAttachment, setRequiresAttachment] = useState(task?.requiresAttachment ? 'true' : 'false');
  const [assignee, setAssignee] = useState({ assigneeId: task?.assignee?.id, assigneeType: task?.assigneeType });
  const [officeAssigneeIds, setOfficeAssigneeIds] = useState(task?.officeAssigneeIds ? task.officeAssigneeIds : {});
  const [notes, setNotes] = useState(task?.notes || '');
  const [dueDate, setDueDate] = useState();
  const [errors, setErrors] = useState();
  const [saving, setSaving] = useState();
  const [createTasks, setCreateTasks] = useState();
  async function handleSave() {
    setSaving(true);
    const updatedTask = {
      office_assignee_ids: officeAssigneeIds,
      name,
      notes,
      required,
      requiresAttachment,
      ...(dueDate),
      ...assignee
    };

    if (isDealPlaybook(playbook)) {
      updatedTask.stage = stage.key;
    } else {
      updatedTask.status = status.key;
    }

    try {
      if (task) {
        updatedTask.id = task.id;
        await updateTask(playbook, task, updatedTask);
      } else {
        updatedTask.createTasks = createTasks;
        await createTask(playbook, updatedTask);
      }
      onHide();
    } catch (errors) {
      setErrors(errors);
      setSaving(false);
    }
  }

  return (
    <Modal className="playbook-tasks-modal" show size="lg" onHide={onHide}>
      <Modal.Header closeButton>
        {task ? 'Edit Task' : 'Add Task'}
      </Modal.Header>

      <Modal.Body>
        <Errors className="mb-3" errors={errors} />

        <ManagedFormGroup
          className="task-name"
          label="Task Name"
          name="name"
          required
          value={name}
          onValueChange={setName}
        />

        {canRequirePlaybookTasks && isDealPlaybook(playbook) &&
          <ManagedFormGroup
            label="Required for Voucher Submission"
            name="required"
            type="select"
            value={required}
            onValueChange={setRequired}
          >
            <option value="false">
              No
            </option>
            <option value="true">
              Yes
            </option>
          </ManagedFormGroup>}

        <ManagedFormGroup
          label="Require Attachment"
          name="requireAttachment"
          type="select"
          value={requiresAttachment}
          onValueChange={setRequiresAttachment}
        >
          <option value="false">
            No
          </option>
          <option value="true">
            Yes
          </option>
        </ManagedFormGroup>

        <ManagedFormGroup
          label="Notes"
          name="notes"
          type="textarea"
          value={notes}
          onValueChange={setNotes}
        />

        <Assignee
          assignee={assignee}
          officeAssigneeIds={officeAssigneeIds}
          offices={offices}
          setAssignee={setAssignee}
          setOfficeAssigneeIds={setOfficeAssigneeIds}
          users={users}
        />

        <DueDates
          playbook={playbook}
          setDueDate={setDueDate}
          task={task}
        />

        {!task &&
          <div className="mt-3">
            <ManagedInput
              name="createTasks"
              type="checkbox"
              value={createTasks}
              onValueChange={setCreateTasks}
            >
              {`
                Add task to existing ${titleize(getPlaybookType(playbook))}
                ${getPlaybookRecordType(playbook)}s (${targetState.label} only)
              `}
            </ManagedInput>
          </div>
        }
      </Modal.Body>

      <Modal.Footer>
        <Button name="cancel" variant="ghost" onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!name || saving} label="Save Task" name="save" onClick={handleSave} />
      </Modal.Footer>
    </Modal>
  );
}

TaskModal.propTypes = {
  canRequirePlaybookTasks: PropTypes.bool,
  createTask: PropTypes.func.isRequired,
  offices: PropTypes.array,
  playbook: PropTypes.shape({
    dealType: PropTypes.string,
    listingType: PropTypes.string,
    tasksPath: PropTypes.string.isRequired,
  }).isRequired,
  stage: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  stages: PropTypes.object.isRequired,
  status: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  task: PropTypes.object,
  updateTask: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  onHide: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  canRequirePlaybookTasks: state.canRequirePlaybookTasks,
  stages: state.stages,
  status: state.status,
  offices: state.offices,
  users: state.users
});

const mapDispatchToProps = {
  createTask,
  updateTask,
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskModal);
